import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { User, Mail, Lock, Save, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import { MFASection } from "./MFASettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(
        `${API}/users/profile`,
        {
          name: form.name,
          email: form.email,
        },
        { withCredentials: true }
      );
      
      await checkAuth(); // Refresh user data
      toast.success("Perfil actualizado exitosamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar perfil");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!form.currentPassword || !form.newPassword) {
      toast.error("Por favor complete todos los campos de contraseña");
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    if (form.newPassword.length < 8) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres");
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/users/change-password`,
        {
          current_password: form.currentPassword,
          new_password: form.newPassword,
        },
        { withCredentials: true }
      );
      
      toast.success("Contraseña cambiada exitosamente");
      setForm({
        ...form,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar contraseña");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <User size={28} className="text-teal-400" />
            <h1 className="text-2xl font-bold text-white">Mi Perfil</h1>
          </div>
          <p className="text-slate-400 text-sm">
            Administra tu información personal y contraseña
          </p>
        </div>

        <div className="space-y-6">
          {/* Personal Information */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <User size={20} className="text-teal-400" />
              <h3 className="text-base font-semibold text-white">Información Personal</h3>
            </div>
            
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <Label className="text-slate-400 text-sm mb-2">Nombre Completo</Label>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Tu nombre completo"
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>

              <div>
                <Label className="text-slate-400 text-sm mb-2">Correo Electrónico</Label>
                <Input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="tu@email.com"
                  className="bg-slate-800 border-slate-700 text-white"
                  required
                />
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-teal-600 hover:bg-teal-500 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      Guardar Cambios
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Change Password */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Lock size={20} className="text-amber-400" />
              <h3 className="text-base font-semibold text-white">Cambiar Contraseña</h3>
            </div>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label className="text-slate-400 text-sm mb-2">Contraseña Actual</Label>
                <Input
                  name="currentPassword"
                  type="password"
                  value={form.currentPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              <div>
                <Label className="text-slate-400 text-sm mb-2">Nueva Contraseña</Label>
                <Input
                  name="newPassword"
                  type="password"
                  value={form.newPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="bg-slate-800 border-slate-700 text-white"
                />
                <p className="text-xs text-slate-500 mt-1">Mínimo 8 caracteres</p>
              </div>

              <div>
                <Label className="text-slate-400 text-sm mb-2">Confirmar Nueva Contraseña</Label>
                <Input
                  name="confirmPassword"
                  type="password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-amber-600 hover:bg-amber-500 text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Cambiando...
                    </>
                  ) : (
                    <>
                      <Lock size={16} className="mr-2" />
                      Cambiar Contraseña
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* User Info Display */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h3 className="text-sm font-semibold text-white mb-3">Información de la Cuenta</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Rol:</span>
                <span className="text-white font-medium capitalize">{user?.role}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">ID de Usuario:</span>
                <span className="text-white font-mono text-xs">{user?.user_id}</span>
              </div>
              {user?.organization_id && (
                <div className="flex justify-between">
                  <span className="text-slate-400">ID de Organización:</span>
                  <span className="text-white font-mono text-xs">{user?.organization_id}</span>
                </div>
              )}
            </div>
          </div>

          {/* MFA inline — moved here from /mfa per product request so all
              security settings live in one place. The standalone /mfa
              route still resolves for bookmarks. */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h3 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
              <Shield size={16} className="text-teal-400" />
              Autenticación Multi-Factor (MFA)
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Protege tu cuenta con verificación en dos pasos (TOTP).
            </p>
            <MFASection />
          </div>
        </div>
      </div>
    </Layout>
  );
}
